/**
 * Чистая логика дерева раскрываемых строк (`subRows`) — без React.
 *
 * Вынесено отдельно от хука по той же причине, что и `expandingOptions`: здесь
 * живут ключи строк и разрешение выбора — места, где ошибка молча ломает
 * массовые действия, и проверять их нужно без рендера.
 */

/** Состояние детей одного раскрытого родителя. */
export interface SubRowsState<T> {
  status: 'loading' | 'ready' | 'error';
  /** Подгруженные дети; при повторной загрузке держим прошлые, пока не пришли новые */
  rows: T[];
  error?: string;
}

/**
 * Префикс ключа раскрываемой строки.
 *
 * Среди детей может оказаться сама сущность родителя (группа из главной и её
 * подчинённых). Под обычным ключом выбор этой сущности лежит у ребёнка, поэтому
 * родителю нужен свой ключ — иначе у двух строк таблицы совпал бы row.id, и
 * галочка ставилась бы сразу на обе.
 */
export const TREE_PARENT_KEY_PREFIX = 'tree:';

/** Ключ раскрываемой строки по ключу её сущности. */
export function treeParentKey(entityKey: string): string {
  return `${TREE_PARENT_KEY_PREFIX}${entityKey}`;
}

/**
 * Ключ строки в дереве: ребёнок — ключ сущности, корневая раскрываемая — с
 * префиксом, обычная корневая — ключ сущности.
 */
export function treeRowKey<T>(params: {
  row: T;
  index: number;
  isChild: boolean;
  canExpand: (row: T) => boolean;
  resolveKey: (row: T, index?: number) => string;
}): string {
  const key = params.resolveKey(params.row, params.index);
  if (params.isChild) return key;
  return params.canExpand(params.row) ? treeParentKey(key) : key;
}

/**
 * Дети строки для TanStack `getSubRows` — ТОЛЬКО у корневых строк.
 *
 * TanStack спрашивает детей рекурсивно, в том числе у самих детей, и глубину
 * при этом не сообщает. Среди детей бывает двойник родителя (главная группы), и
 * `canExpand` для него тоже true — без проверки «это корень» он «раскрылся» бы
 * в ту же группу, где снова он же: бесконечная рекурсия и «Maximum call stack
 * size exceeded» на первом же раскрытии.
 *
 * Корень узнаём по identity объекта: дети приходят из `load` другими объектами,
 * даже если это та же сущность.
 *
 * @param rootRows корневые строки страницы (объекты, отданные таблице)
 * @param childrenByParent дети по ключу родителя (`tree:…`)
 */
export function resolveSubRows<T>(params: {
  row: T;
  index: number;
  rootRows: ReadonlySet<T>;
  childrenByParent: Record<string, SubRowsState<T>>;
  canExpand: (row: T) => boolean;
  resolveKey: (row: T, index?: number) => string;
}): T[] | undefined {
  const { row, index, rootRows, childrenByParent, canExpand, resolveKey } = params;
  if (!rootRows.has(row) || !canExpand(row)) return undefined;
  const key = treeRowKey({ row, index, isChild: false, canExpand, resolveKey });
  const state = childrenByParent[key];
  return state && state.rows.length > 0 ? state.rows : undefined;
}

/**
 * Строки, которые можно выбрать в дереве: обычные корневые и подгруженные дети.
 * Раскрываемые корни не выбираются (это узлы-группы). Повторы по ключу
 * отбрасываются — одна сущность в выборе должна быть одной строкой.
 *
 * @param rows корневые строки страницы
 * @param childrenByParent дети по ключу родителя (`tree:…`)
 */
export function collectSelectableRows<T>(params: {
  rows: T[];
  childrenByParent: Record<string, SubRowsState<T>>;
  canExpand: (row: T) => boolean;
  resolveKey: (row: T, index?: number) => string;
}): { key: string; row: T }[] {
  const { rows, childrenByParent, canExpand, resolveKey } = params;
  const seen = new Set<string>();
  const result: { key: string; row: T }[] = [];
  const push = (key: string, row: T) => {
    if (seen.has(key)) return;
    seen.add(key);
    result.push({ key, row });
  };

  rows.forEach((row, index) => {
    const key = resolveKey(row, index);
    if (!canExpand(row)) {
      push(key, row);
      return;
    }
    const children = childrenByParent[treeParentKey(key)];
    children?.rows.forEach((child, childIndex) => push(resolveKey(child, childIndex), child));
  });
  return result;
}

/**
 * Раскрытые родители, которые пережили перезагрузку страницы: они снова на
 * странице и всё ещё раскрываемы. Их раскрытие сохраняем, а детей грузим
 * заново — старые могли устареть (кого-то вывели из группы, добавили фото).
 *
 * @returns ключ родителя и его актуальная строка
 */
export function survivingExpandedParents<T>(params: {
  expanded: Record<string, boolean>;
  rows: T[];
  canExpand: (row: T) => boolean;
  resolveKey: (row: T, index?: number) => string;
}): { key: string; row: T }[] {
  const { expanded, rows, canExpand, resolveKey } = params;
  const result: { key: string; row: T }[] = [];
  rows.forEach((row, index) => {
    if (!canExpand(row)) return;
    const key = treeParentKey(resolveKey(row, index));
    if (expanded[key]) result.push({ key, row });
  });
  return result;
}

/**
 * Ключи детей, которые совпали с ключами корневых строк страницы.
 *
 * Такое бывает, когда ребёнок виден и на верхнем уровне (например, фильтр
 * «показать всех» вывел подчинённые отдельными строками). Две строки с одним
 * ключом — один чекбокс на двоих, поэтому о коллизии надо знать: сама таблица
 * её не разрулит.
 */
export function findChildKeyCollisions<T>(params: {
  rows: T[];
  childrenByParent: Record<string, SubRowsState<T>>;
  canExpand: (row: T) => boolean;
  resolveKey: (row: T, index?: number) => string;
}): string[] {
  const { rows, childrenByParent, canExpand, resolveKey } = params;
  const rootKeys = new Set(
    rows.flatMap((row, index) => (canExpand(row) ? [] : [resolveKey(row, index)]))
  );
  const collisions = new Set<string>();
  for (const state of Object.values(childrenByParent)) {
    state.rows.forEach((child, index) => {
      const key = resolveKey(child, index);
      if (rootKeys.has(key)) collisions.add(key);
    });
  }
  return [...collisions];
}
